import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { verifyAndCompletePayment, api } from "../api";
import jsPDF from "jspdf";
import "../App.css";
// Constants
const SUPPORT_CONTACT = {
  PHONE: '+91 8866822366',
  NAME: 'AastroG Support'
};

function PaymentResult() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshCredits, token, loading: authLoading } = useAuth();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Processing your payment...");
  const [creditsAdded, setCreditsAdded] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const receiptRef = useRef(null);

  // ✅ COMPREHENSIVE AUTH LOGGER - Runs on mount
  useEffect(() => {
    console.log('🚀 === PAYMENT RESULT PAGE LOADED ===');
    console.log('📍 URL:', window.location.href);
    console.log('🔍 Search Params:', {
      status: searchParams.get("status"),
      txnid: searchParams.get("txnid"),
      amount: searchParams.get("amount"),
      error: searchParams.get("error"),
      mode: searchParams.get("mode")
    });

    // ✅ AUTH CONTEXT STATUS CHECK
    console.log('🔐 === AUTH CONTEXT DEBUG ===');
    console.log('👤 User object:', user);
    console.log('🆔 User ID:', user?.userId);
    console.log('📧 User Email:', user?.email);
    console.log('💎 User Credits:', user?.credits);
    console.log('🔑 Auth Token exists:', !!token);
    console.log('⏳ Auth Loading:', authLoading);
    console.log('📦 LocalStorage User:', localStorage.getItem('astroguru_user') ? 'EXISTS' : 'MISSING');
    console.log('🔐 LocalStorage Token:', localStorage.getItem('astroguru_token') ? 'EXISTS' : 'MISSING');
    console.log('==============================');

    // ✅ Check if user is completely missing
    if (!user) {
      console.error('❌ NO USER IN AUTH CONTEXT - User might need to login');
    } else if (!user.userId) {
      console.warn('⚠️ USER EXISTS BUT NO userId - Sync might fail');
    } else {
      console.log('✅ USER AUTHENTICATED WITH userId:', user.userId);
    }
  }, [user, token, authLoading, searchParams]);

  useEffect(() => {
    const fetchPaymentData = async () => {
      const paymentStatus = searchParams.get("status");
      const txnid = searchParams.get("txnid");
      const amount = searchParams.get("amount");
      const error = searchParams.get("error");
      const orderIdFromUrl = searchParams.get("orderId");

      console.log("💳 Payment Result:", { paymentStatus, txnid, amount, error });

      // ✅ CHECK: Is this a cart order payment or credit purchase?
      const pendingOrderId = sessionStorage.getItem('pendingOrderId');
      const pendingOrderNumber = sessionStorage.getItem('pendingOrderNumber');
      const isCartOrderPayment = !!pendingOrderId || !!orderIdFromUrl;

      console.log("🛒 Payment Type:", {
        isCartOrderPayment,
        pendingOrderId,
        pendingOrderNumber,
        orderIdFromUrl
      });

      // ═════════════════════════════════════════════════════════════════════════
      // CART ORDER PAYMENT FLOW
      // ═════════════════════════════════════════════════════════════════════════
      if (isCartOrderPayment) {
        // ✅ VALIDATION: Check if PayU callback parameters exist
        if (!txnid || !paymentStatus) {
          console.warn('⚠️ pendingOrderId exists but no PayU callback parameters found');
          console.log('🧹 Clearing stale sessionStorage data');
          
          // Clear stale session data
          sessionStorage.removeItem('pendingOrderId');
          sessionStorage.removeItem('pendingOrderNumber');
          
          // Show user-friendly message
          setStatus("error");
          setMessage("Payment Incomplete");
          setErrorMessage("Your payment was not completed. Please try placing your order again from the cart.");
          setCreditsAdded(0);
          return; // Exit early
        }

        try {
          console.log('🛒 Processing cart order payment verification...');

          const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
          const successfulPaymentStates = ['completed', 'success', 'paid'];
          const successfulOrderStates = ['processing', 'shipped', 'delivered', 'completed'];

          const checkOrderCompletion = async (orderIdToCheck) => {
            const orderResponse = await api.get(`/aastrog_cart/orders/${orderIdToCheck}`);
            const orderData = orderResponse?.data?.data || {};
            const paymentStatusValue = orderData?.paymentStatus || orderData?.PaymentStatus || orderData?.payment_status || '';
            const orderStatusValue = orderData?.orderStatus || orderData?.OrderStatus || orderData?.order_status || '';

            const normalizedPaymentStatus = String(paymentStatusValue).toLowerCase();
            const normalizedOrderStatus = String(orderStatusValue).toLowerCase();
            const isCompleted = successfulPaymentStates.includes(normalizedPaymentStatus) || successfulOrderStates.includes(normalizedOrderStatus);

            return {
              isCompleted,
              orderData,
              normalizedPaymentStatus,
              normalizedOrderStatus
            };
          };

          // Prepare PayU callback data from URL params
          const payuCallbackData = {};
          searchParams.forEach((value, key) => {
            payuCallbackData[key] = value;
          });

          if (!payuCallbackData.orderId && pendingOrderId) {
            payuCallbackData.orderId = pendingOrderId;
          }

          const resolvedOrderId = payuCallbackData.orderId || payuCallbackData.udf1 || pendingOrderId || orderIdFromUrl;
          const hasFullPayUCallbackPayload = Boolean(
            payuCallbackData.hash &&
            payuCallbackData.txnid &&
            payuCallbackData.status &&
            payuCallbackData.amount &&
            (payuCallbackData.udf1 || payuCallbackData.orderId)
          );

          console.log('📤 Sending PayU callback data to backend:', payuCallbackData);

          // Call callback endpoint only when full signed PayU payload is available.
          // Otherwise verify from order status because frontend redirect URL contains simplified params.
          let response;
          if (hasFullPayUCallbackPayload) {
            response = await api.post('/aastrog_cart/payment/verify-callback', payuCallbackData);
          } else {
            if (!resolvedOrderId) {
              throw new Error('Missing orderId for cart payment verification');
            }

            let verification = await checkOrderCompletion(resolvedOrderId);

            if (!verification.isCompleted && String(paymentStatus || '').toLowerCase() === 'success') {
              for (let attempt = 1; attempt <= 4; attempt += 1) {
                await delay(1500);
                verification = await checkOrderCompletion(resolvedOrderId);
                if (verification.isCompleted) {
                  break;
                }
              }
            }

            response = {
              data: {
                success: verification.isCompleted,
                message: verification.isCompleted ? 'Payment verified from order status' : 'Payment not completed',
                data: {
                  orderId: Number(resolvedOrderId),
                  orderNumber: verification.orderData?.orderNumber || verification.orderData?.OrderNumber
                },
                error: verification.isCompleted ? null : `Payment still pending or failed (paymentStatus=${verification.normalizedPaymentStatus || 'unknown'}, orderStatus=${verification.normalizedOrderStatus || 'unknown'})`
              }
            };
          }

          console.log('✅ Cart payment verification response:', response.data);

          if (response.data.success) {
            // Payment successful for cart order
            console.log('🎉 CART ORDER PAYMENT SUCCESS');
            console.log('📦 Order Number:', response.data.data.orderNumber);
            console.log('💳 Payment ID:', response.data.data.paymentId);

            // Redirect to order confirmation page
            const orderId = response.data.data.orderId;
            navigate(`/order-confirmation/${orderId}`, { replace: true });
            return; // Exit early, don't set loading/error states

          } else {
            // Payment failed
            console.log('❌ CART ORDER PAYMENT FAILED:', response.data.message);
            setStatus("failure");
            setMessage(response.data.message || "Payment Failed");
            setErrorMessage(response.data.error || "Payment was not completed");
            setCreditsAdded(0);
            setPaymentMode(searchParams.get("mode") || null);

            // Clear pending order from sessionStorage
            sessionStorage.removeItem('pendingOrderId');
            sessionStorage.removeItem('pendingOrderNumber');
          }

        } catch (err) {
          console.error("❌ Cart order payment verification error:", err);
          setStatus("error");
          setMessage(err.response?.data?.message || "Payment Verification Failed");
          setErrorMessage(err.response?.data?.error || "Failed to verify payment");
          setCreditsAdded(0);
          setPaymentMode(searchParams.get("mode") || null);

          // Clear pending order from sessionStorage
          sessionStorage.removeItem('pendingOrderId');
          sessionStorage.removeItem('pendingOrderNumber');
        }

        return; // Exit early for cart order flow
      }

      // ═════════════════════════════════════════════════════════════════════════
      // CREDIT PURCHASE PAYMENT FLOW (EXISTING)
      // ═════════════════════════════════════════════════════════════════════════

      // ✅ STEP 1: Always verify with backend first (for both success AND failure)
      if (txnid) {
        try {
          console.log('🔍 Verifying payment with backend:', txnid);
          console.log('👤 Current user before verification:', user?.userId || 'NO USER');
          
          // ✅ CRITICAL: Call verify-complete endpoint
          const response = await verifyAndCompletePayment(txnid);
          
          console.log('✅ Verification response:', response.data);
          console.log('✅ Error message from backend:', response.data.errorMessage);

          // ✅ Handle SUCCESS
          if (response?.data?.success) {
            console.log('🎉 PAYMENT SUCCESS - Credits to be added:', response.data.creditsAdded);
            setStatus("success");
            setMessage("Payment Successful!");
            setCreditsAdded(response.data.creditsAdded);
            
            // Get payment mode
            const mode = response.data.paymentDetails?.mode || 
                        response.data.paymentDetails?.paymentMode ||
                        searchParams.get("mode");
            setPaymentMode(mode || null);

            // Refresh credits
            if (user?.userId) {
              try {
                console.log('🔄 Refreshing credits for user:', user.userId);
                await refreshCredits();
                console.log('✅ Credits refreshed successfully');
              } catch (refreshError) {
                console.error('❌ Failed to refresh credits:', refreshError);
              }
            } else {
              console.error('❌ NO USER ID - Cannot refresh credits');
            }
          } 
          // ✅ Handle FAILURE (verified with PayU but failed)
          else if (response?.data?.verified && !response.data.success) {
            console.log('❌ PAYMENT FAILED - Backend verified but failed');
            setStatus("failure");
            setMessage(response.data.message || "Payment Failed");
            setCreditsAdded(0);
            // ✅ STORE ERROR MESSAGE FROM BACKEND
            setErrorMessage(
              response.data.errorMessage || 
              response.data.error || 
              searchParams.get("error") ||
              "Payment was not completed"
            );
            // Get payment mode from backend or URL
            const mode = response.data.paymentDetails?.mode || 
                        searchParams.get("mode");
            setPaymentMode(mode || null);
            console.log('❌ Payment failed:', response.data.status);
            console.log('❌ Error message:', response.data.errorMessage);
          } 
          // ✅ Handle PENDING
          else if (response?.data?.status === 'pending') {
            console.log('⏳ PAYMENT PENDING');
            setStatus("loading");
            setMessage("Payment is being processed...");
            setCreditsAdded(0);
            setPaymentMode(searchParams.get("mode") || null);
          } 
          // ✅ Handle VERIFICATION ERROR
          else {
            console.log('⚠️ PAYMENT VERIFICATION ERROR');
            setStatus("error");
            setMessage("Payment Verification Failed");
            setCreditsAdded(0);
            setPaymentMode(searchParams.get("mode") || null);
          }

        } catch (err) {
          console.error("❌ Failed to verify payment:", err);
          
          // Set error state
          setStatus("error");
          setMessage(err.response?.data?.message || "Payment Verification Failed");
          setCreditsAdded(0);
          setPaymentMode(searchParams.get("mode") || null);
        }
      } else {
        // No transaction ID
        console.error('❌ NO TRANSACTION ID FOUND');
        setStatus("error");
        setMessage("Invalid Payment Request");
      }
    };

    fetchPaymentData();
  }, [searchParams, navigate, user, refreshCredits]);

  // ✅ POST-PAYMENT AUTH STATUS MONITOR
  useEffect(() => {
    if (status === 'success' && creditsAdded !== null) {
      console.log('🔄 === POST-PAYMENT AUTH STATUS ===');
      console.log('👤 User:', user?.email || 'No user');
      console.log('💎 Credits after payment:', user?.credits);
      console.log('🆔 User ID:', user?.userId);
      console.log('==============================');
    }
  }, [status, creditsAdded, user]);

  const getCurrentTimestamp = () => {
    return new Date().toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const generatePDFReceipt = async () => {
    setIsGeneratingPDF(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 16;
      let y = margin;

      // Colors
      const success = { r: 16, g: 185, b: 129 }; // green

      // Header/Footer image URLs (from env)
      const headerImageUrl = process.env.REACT_APP_HEADER_IMG_URL || 'https://res.cloudinary.com/ddgd5aq1u/image/upload/v1765171545/3_whrwh6.png';
      const footerImageUrl = process.env.REACT_APP_FOOTER_IMG_URL || 'https://res.cloudinary.com/ddgd5aq1u/image/upload/v1765282410/Footer4_sxuxzo.png';

      // Helper to load an image. Resolve with Image or null on error to continue gracefully.
      const loadImage = (src) => new Promise((resolve) => {
        if (!src) return resolve(null);
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
      });

      // Attempt to load header image (but proceed if it fails)
      const headerImg = await loadImage(headerImageUrl);
      const headerHeight = 34;
      const footerHeight = 28;
      if (headerImg) {
        try {
          pdf.addImage(headerImg, 'PNG', 0, 0, pageWidth, headerHeight);
          y = headerHeight + 15;
        } catch (addErr) {
          console.warn('Header image could not be added to PDF, continuing without it.', addErr);
          y = 20;
        }
      } else {
        y = 20;
      }

      // If a custom template renderer is available (e.g. user-provided), prefer it.
      // The renderer should be a global async function that returns a dataURL PNG of the full receipt.
      try {
        if (typeof window !== 'undefined' && typeof window.renderPaymentTemplate === 'function') {
          const templateDataUrl = await window.renderPaymentTemplate({
            amount: searchParams.get('amount') || '-',
            customerName: user?.fullname || user?.name || 'Customer',
            customerEmail: user?.email || '',
            txnid: searchParams.get('txnid') || '-',
            dateStr: getCurrentTimestamp(),
            paymentMode,
            creditsAdded
          });

          if (templateDataUrl) {
            const imgType = templateDataUrl.indexOf('image/png') !== -1 ? 'PNG' : 'JPEG';
            try {
              // Draw the template image full-width leaving a small margin top if header exists
              const topOffset = headerImg ? headerHeight : 0;
              pdf.addImage(templateDataUrl, imgType, 0, topOffset, pageWidth, pageHeight - topOffset - footerHeight);
              const txnid = searchParams.get('txnid') || '-';
              pdf.save(`AastroG-Receipt-${txnid}.pdf`);
              setIsGeneratingPDF(false);
              return;
            } catch (e) {
              console.warn('Custom template image could not be added, falling back to internal renderer.', e);
            }
          }
        }
      } catch (e) {
        console.warn('Error while invoking custom template renderer', e);
      }

      // Title: Payment Receipt
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      pdf.text('Payment Receipt', pageWidth / 2, y, { align: 'center' });

      y += 12;

      // Receipt meta box
      pdf.setTextColor(0, 0, 0);
      pdf.setDrawColor(200, 200, 200);
      pdf.roundedRect(margin, y, pageWidth - margin * 2, 22, 2, 2, 'S');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.text('Receipt Details', margin + 4, y + 8);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      const txnid = searchParams.get('txnid') || '-';
      const dateStr = getCurrentTimestamp();
      pdf.text(`Receipt No: ${txnid}`, margin + 4, y + 16);
      pdf.text(`Date: ${dateStr}`, pageWidth - margin - 65, y + 16);

      y += 30;

      // Billed To box
      pdf.setDrawColor(200, 200, 200);
      pdf.roundedRect(margin, y, pageWidth - margin * 2, 30, 2, 2, 'S');

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.text('Billed To', margin + 4, y + 8);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      const customerName = (user?.fullname || user?.name || 'Customer');
      const customerEmail = (user?.email || 'N/A');
      pdf.text(customerName, margin + 4, y + 16);
      pdf.text(customerEmail, margin + 4, y + 23);

      y += 38;

      // Payment summary box
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.text('Payment Summary', margin, y);
      y += 2;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 8;

      const amount = searchParams.get('amount') || '-';
      const credits = (creditsAdded ?? '-') + (creditsAdded != null ? ' Credits' : '');
      const statusText = 'Success';

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      
      pdf.text('Transaction ID:', margin, y);
      pdf.text(txnid, margin + 50, y);
      y += 8;
      
      pdf.text('Amount Paid:', margin, y);
      pdf.text(`Rs. ${amount}`, margin + 50, y);
      y += 8;
      
      pdf.text('Credits Added:', margin, y);
      pdf.text(credits, margin + 50, y);
      y += 8;
      
      pdf.text('Payment Mode:', margin, y);
      pdf.text(paymentMode ? paymentMode : 'N/A', margin + 50, y);
      y += 8;
      
      pdf.text('Payment Status:', margin, y);
      pdf.setTextColor(success.r, success.g, success.b);
      pdf.setFont('helvetica', 'bold');
      pdf.text(statusText, margin + 50, y);
      pdf.setTextColor(0, 0, 0);

      y += 16;

      // Footer note box
      pdf.setDrawColor(200, 200, 200);
      pdf.roundedRect(margin, y, pageWidth - margin * 2, 26, 2, 2, 'S');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.text('Thank you for your purchase!', margin + 4, y + 9);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || 'support@astroguru.com';
      const supportText = `For any queries, share your Transaction ID with ${supportEmail} for quick resolution.`;
      const wrapped = pdf.splitTextToSize(supportText, pageWidth - margin * 2 - 8);
      pdf.text(wrapped, margin + 4, y + 18);

      // Attempt to load footer image (skip if it fails)
      const footerImg = await loadImage(footerImageUrl);
      if (footerImg) {
        try {
          pdf.addImage(footerImg, 'PNG', 0, pageHeight - footerHeight, pageWidth, footerHeight);
        } catch (addErr) {
          console.warn('Footer image could not be added to PDF, continuing without it.', addErr);
        }
      }

      // Save
      pdf.save(`AastroG-Receipt-${txnid}.pdf`);
    } catch (err) {
      console.error('❌ Error generating PDF:', err);
      alert('Failed to generate receipt. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="fb-page-wrapper">
      <div className="fb-main-container">
        <div className="fb-top-section">
          <button className="fb-close-button" onClick={() => navigate("/home")}>
            ×
          </button>

          {/* Logo and Brand Header - Top of everything */}
          <div className="payment-brand-header">
            <div className="payment-logo"></div>
            <h2 className="payment-brand-name">AastroG</h2>
          </div>

          {/* Payment Successful heading for success state */}
          {status === "success" && (
            <h1 className="heading" style={{ color: "#ffd700", marginTop: "25px", marginBottom: "20px" }}>{message}</h1>
          )}

          {/* Payment Amount Display - Paytm Style */}
          {status === "success" && (
            <div className="payment-amount-display">
              <span className="payment-amount-text">₹{searchParams.get("amount")}</span>
              <div className="amount-badge-checkmark">
                <span className="badge-check-icon">✓</span>
              </div>
            </div>
          )}

          {status === "loading" && <div className="fb-type-emoji">⏳</div>}

          {/* Failure Icon */}
          {status === "failure" && (
            <div className="failure-icon">
              <span className="failure-icon-symbol">✕</span>
            </div>
          )}

          {/* Error Icon */}
          {status === "error" && (
            <div className="error-icon">
              <span className="error-icon-symbol">⚠</span>
            </div>
          )}

          {/* Heading for non-success states */}
          {status !== "success" && <h1 className="heading">{message}</h1>}
        </div>

        <div className="fb-input-form">
          {status === "success" && (
            <>
              {/* Receipt Content - This will be captured for PDF */}
              <div ref={receiptRef}>
              {/* Payment Details Card */}
              <div className="payment-details-card">
                <div className="fb-form-group">
                  <div className="payment-info-row">
                    <span className="payment-info-label" style={{ fontSize: "16px" }}>Transaction ID</span>
                    <span className="payment-info-value" style={{ fontSize: "16px" }}>
                      {searchParams.get("txnid")}
                    </span>
                  </div>

                  <div className="payment-info-row">
                    <span className="payment-info-label" style={{ fontSize: "16px" }}>Amount Paid</span>
                    <span className="payment-info-value payment-amount" style={{ fontSize: "18px" }}>
                      ₹{searchParams.get("amount")}
                    </span>
                  </div>

                  <div
                    className="payment-info-row"
                    style={{ borderBottom: "none" }}
                  >
                    <span className="payment-info-label" style={{ fontSize: "16px" }}>
                      Credits Added
                    </span>
                    <span
                      className="payment-info-value"
                      style={{ color: "#10b981", fontSize: "18px", fontWeight: "700" }}
                    >
                      +{creditsAdded !== null ? creditsAdded : '...'} Credits
                    </span>
                  </div>
                </div>

                {/* Timestamp */}
                <div className="payment-timestamp" style={{ fontSize: "15px" }}>
                  <span>{getCurrentTimestamp()}</span>
                </div>
              </div>
              </div>

              {/* Download Button - PDF only */}
              <div style={{ textAlign: "center", marginTop: "8px" }}>
                <button
                  className="download-receipt-btn"
                  onClick={generatePDFReceipt}
                  disabled={isGeneratingPDF}
                  style={{ width: "100%", maxWidth: "500px", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <span style={{ fontSize: "16px", fontWeight: 700 }}>
                    {isGeneratingPDF ? 'Generating...' : 'Download Receipt'}
                  </span>
                </button>
              </div>
            </>
          )}

          {status === "failure" && (
            <>
              {/* Payment Details Card */}
              <div className="payment-details-card">
                <div className="fb-form-group">
                  <div className="payment-info-row">
                    <span className="payment-info-label" style={{ fontSize: "18px", fontWeight: "600" }}>Transaction ID</span>
                    <span className="payment-info-value" style={{ fontSize: "18px" }}>
                      {searchParams.get("txnid")}
                    </span>
                  </div>

                  {searchParams.get("amount") && (
                    <div className="payment-info-row">
                      <span className="payment-info-label" style={{ fontSize: "18px", fontWeight: "600" }}>
                        Attempted Amount
                      </span>
                      <span className="payment-info-value" style={{ fontSize: "18px" }}>
                        ₹{searchParams.get("amount")}
                      </span>
                    </div>
                  )}

                  {/* ✅ ALWAYS SHOW ERROR SECTION FOR FAILURE */}
                  <div
                    className="payment-info-row"
                    style={{ borderBottom: "none" }}
                  >
                    <span className="payment-info-label" style={{ fontSize: "18px", fontWeight: "600" }}>Error Reason</span>
                    <span className="payment-info-value payment-error" style={{ fontSize: "18px" }}>
                      {/* ✅ USE errorMessage STATE, NOT URL PARAM */}
                      {errorMessage || "Payment was not completed"}
                    </span>
                  </div>
                </div>

                {/* Timestamp */}
                <div className="payment-timestamp" style={{ fontSize: "16px" }}>
                  <span>{getCurrentTimestamp()}</span>
                </div>

                {/* Retry Payment Button */}
                <div style={{ textAlign: "center", marginTop: "20px" }}>
                  <button
                    className="retry-payment-btn"
                    onClick={() => {
                      // Check if this was a cart order payment
                      const wasCartOrder = sessionStorage.getItem('pendingOrderId') || 
                                          window.location.search.includes('orderId');
                      if (wasCartOrder) {
                        navigate("/cart"); // Retry cart checkout
                      } else {
                        navigate("/credits"); // Retry credit purchase
                      }
                    }}
                  >
                    <span>Retry Payment</span>
                  </button>
                </div>
              </div>

              {/* Help Section */}
              <div className="payment-help-box">
                <p className="payment-help-title" style={{ fontSize: "20px", fontWeight: "600" }}>Need Help?</p>
                <p className="payment-help-text" style={{ fontSize: "16px" }}>
                  If your account was debited, please wait 24-48 hours for
                  auto-refund or contact support
                </p>
                <button
                  className="report-issue-btn"
                  onClick={() => {
                    const issueDetails = {
                      type: 'Payment Failed',
                      transactionId: searchParams.get("txnid"),
                      amount: searchParams.get("amount"),
                      error: searchParams.get("error"),
                      timestamp: getCurrentTimestamp()
                    };
                    navigate('/feedback', { state: { paymentIssue: issueDetails } });
                  }}
                >
                  <span>Report Payment Issue</span>
                </button>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              {/* Error Message Banner */}
              <div className="error-message-banner">
                <div className="error-label" style={{ fontSize: "20px", fontWeight: "700" }}>Verification Failed</div>
                <div className="error-subtitle" style={{ fontSize: "16px" }}>
                  We couldn't verify your payment status
                </div>
              </div>

              {/* Payment Details Card */}
              <div className="payment-details-card">
                <div className="fb-form-group">
                  <div
                    className="payment-info-row"
                    style={{ borderBottom: "none" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <span className="payment-info-label" style={{ fontSize: "18px", fontWeight: "600" }}>Transaction ID</span>
                    </div>
                    <span
                      className="payment-info-value"
                      style={{ wordBreak: "break-all" }}
                    >
                      {searchParams.get("txnid")}
                    </span>
                  </div>
                </div>

                {/* Timestamp */}
                <div className="payment-timestamp">
                  <span>{getCurrentTimestamp()}</span>
                </div>

                {/* Support Contact Box */}
                <div className="payment-support-box" style={{ background: "rgba(37, 99, 235, 0.15)", borderColor: "#2563eb" }}>
                  <p className="payment-support-label" style={{ fontSize: "18px", fontWeight: "600", color: "#2563eb" }}>Contact Support</p>
                  <p className="payment-support-number" style={{ fontSize: "20px", fontWeight: "700" }}>{SUPPORT_CONTACT.PHONE}</p>
                  <p className="payment-support-name" style={{ fontSize: "16px" }}>{SUPPORT_CONTACT.NAME}</p>
                  <p
                    className="payment-support-text"
                    style={{
                      fontSize: "15px",
                      color: "rgba(255, 255, 255, 0.7)",
                      marginTop: "10px",
                    }}
                  >
                    Share the transaction ID above for quick resolution
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Action Buttons */}
          {status !== "loading" && (
            <div className="fb-button-group">
              <button
                type="button"
                className="credit-package"
                onClick={() => navigate("/home")}
              >
                <span className="fb-type-heading">Home</span>
              </button>

              <button
                type="button"
                className="credit-package"
                onClick={() => navigate("/credits")}
              >
                <span className="fb-type-heading">Credits</span>
              </button>

              <button
                type="button"
                className="credit-package"
                onClick={() => navigate("/payment/history")}
              >
                <span className="fb-type-heading">Payment History</span>
              </button>
            </div>
          )}

          {/* Security Info */}
          <p className="refer-subtitle nomargin">
            🔒 Secure payment powered by PayU • 256-bit SSL encryption
          </p>
        </div>
      </div>
    </div>
  );
}

export default PaymentResult;